import type {
  NetworkDashboardAvailableStore,
  NetworkDashboardStoreAgeFilter,
  StoreAgeBracket,
} from '@gozoki/api-types'
import { DateTime } from 'luxon'
import { setsEqual } from '~/lib/sets'

/** Ancienneté UI state ↔ API `NetworkDashboardStoreAgeFilter` + optional explicit `ageStoreIds`. */
export type StoreAgeLocalState =
  | { mode: 'default' }
  | { mode: 'custom'; selectedStoreIds: Set<number> }

export interface NetworkDashboardAgeFilterApplyResult {
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  /**
   * When set, the KPI request includes `ageStoreIds` and the backend intersects with geographic
   * `storeIds` instead of applying opening-date presets on SQL.
   */
  ageStoreIds: number[] | undefined
}

export const STORE_AGE_BOUNDS_MONTHS = [3, 6, 12] as const

const matureThreshold = (referenceDate: string): string =>
  DateTime.fromISO(referenceDate).minus({ months: STORE_AGE_BOUNDS_MONTHS[2] }).toISODate()!

export function storeAgeBracketAt(
  periodEndIso: string,
): (store: NetworkDashboardAvailableStore) => StoreAgeBracket {
  const [months3, months6] = STORE_AGE_BOUNDS_MONTHS
  const threshold3 = DateTime.fromISO(periodEndIso).minus({ months: months3 }).toISODate()!
  const threshold6 = DateTime.fromISO(periodEndIso).minus({ months: months6 }).toISODate()!
  const threshold12 = matureThreshold(periodEndIso)
  return ({ ageReferenceDate }) => {
    if (ageReferenceDate <= threshold12) return 'more_than_12_months'
    if (ageReferenceDate > threshold3) return 'less_than_3_months'
    if (ageReferenceDate > threshold6) return 'between_3_and_6_months'
    return 'between_6_and_12_months'
  }
}

export function isNewAt(dateIso: string): (store: NetworkDashboardAvailableStore) => boolean {
  const bracketOf = storeAgeBracketAt(dateIso)
  return (store) => bracketOf(store) === 'less_than_3_months'
}

const PARTITION_KEYS = {
  less_than_3_months: 'lessThan3',
  between_3_and_6_months: 'between3And6',
  between_6_and_12_months: 'between6And12',
  more_than_12_months: 'moreThan12',
} as const satisfies Record<StoreAgeBracket, string>

type OpeningMaturityPartition = Record<
  (typeof PARTITION_KEYS)[StoreAgeBracket],
  NetworkDashboardAvailableStore[]
>

export function partitionStoresByOpeningMaturity(
  stores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): OpeningMaturityPartition {
  const bracketOf = storeAgeBracketAt(periodEndIso)
  const parts: OpeningMaturityPartition = {
    lessThan3: [],
    between3And6: [],
    between6And12: [],
    moreThan12: [],
  }
  for (const store of stores) parts[PARTITION_KEYS[bracketOf(store)]].push(store)
  return parts
}

export function storeAgeFromFilter(
  filter: NetworkDashboardStoreAgeFilter,
  ageStoreIds: number[] | undefined,
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): StoreAgeLocalState {
  if (filter === 'default') {
    return { mode: 'default' }
  }

  const parts = partitionStoresByOpeningMaturity(availableStores, periodEndIso)
  const lessThan3Ids = new Set(parts.lessThan3.map((s) => s.id))
  const between3And6Ids = new Set(parts.between3And6.map((s) => s.id))
  const between6And12Ids = new Set(parts.between6And12.map((s) => s.id))
  const moreThan12Ids = new Set(parts.moreThan12.map((s) => s.id))

  const fullIds = new Set<number>([
    ...lessThan3Ids,
    ...between3And6Ids,
    ...between6And12Ids,
    ...moreThan12Ids,
  ])

  if (ageStoreIds !== undefined) {
    return { mode: 'custom', selectedStoreIds: new Set(ageStoreIds) }
  }
  if (filter === 'all') {
    return { mode: 'custom', selectedStoreIds: fullIds }
  }
  if (filter === 'less_than_3_months') {
    return { mode: 'custom', selectedStoreIds: lessThan3Ids }
  }
  if (filter === 'between_3_and_6_months') {
    return { mode: 'custom', selectedStoreIds: between3And6Ids }
  }
  if (filter === 'between_6_and_12_months') {
    return { mode: 'custom', selectedStoreIds: between6And12Ids }
  }
  if (filter === 'more_than_12_months') {
    return { mode: 'custom', selectedStoreIds: moreThan12Ids }
  }
  return { mode: 'custom', selectedStoreIds: lessThan3Ids }
}

export function applyResultFromStoreAgeState(
  state: StoreAgeLocalState,
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): NetworkDashboardAgeFilterApplyResult {
  if (state.mode === 'default') {
    return { storeAgeFilter: 'default', ageStoreIds: undefined }
  }

  const parts = partitionStoresByOpeningMaturity(availableStores, periodEndIso)
  const lessThan3Ids = new Set(parts.lessThan3.map((s) => s.id))
  const between3And6Ids = new Set(parts.between3And6.map((s) => s.id))
  const between6And12Ids = new Set(parts.between6And12.map((s) => s.id))
  const moreThan12Ids = new Set(parts.moreThan12.map((s) => s.id))

  const fullIds = new Set<number>([
    ...lessThan3Ids,
    ...between3And6Ids,
    ...between6And12Ids,
    ...moreThan12Ids,
  ])

  let selected = state.selectedStoreIds
  if (selected.size === 0) {
    selected = fullIds
  }

  if (setsEqual(selected, fullIds)) {
    return { storeAgeFilter: 'all', ageStoreIds: undefined }
  }
  if (setsEqual(selected, lessThan3Ids)) {
    return { storeAgeFilter: 'less_than_3_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, between3And6Ids)) {
    return { storeAgeFilter: 'between_3_and_6_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, between6And12Ids)) {
    return { storeAgeFilter: 'between_6_and_12_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, moreThan12Ids)) {
    return { storeAgeFilter: 'more_than_12_months', ageStoreIds: undefined }
  }
  return {
    storeAgeFilter: 'all',
    ageStoreIds: [...selected].sort((a, b) => a - b),
  }
}
